import { useCallback, useEffect, useRef } from "react";
import { useInterviewState } from "./InterviewProvider";
import { api, type FieldSummaryEntry } from "../api/client";
import { nextTarget } from "../engine/completion";
import type { BooleanWithNotesValue, InputMode, InterviewState, VerbatimEntry } from "../engine/types";

const TRANSCRIPT_WINDOW = 16;

function wireValue(value: unknown): unknown {
  if (value && typeof value === "object" && "flag" in (value as Record<string, unknown>)) {
    return (value as BooleanWithNotesValue).flag;
  }
  if (Array.isArray(value) && value.length > 0 && typeof value[0] === "object" && value[0] !== null && "text" in value[0]) {
    return (value as VerbatimEntry[]).map((e) => e.text);
  }
  return value;
}

/** Exported so other callers (e.g. the AI-summary UI) build the exact same wire shape rather than a parallel, possibly-drifting copy. */
export function buildFieldSummary(state: InterviewState): FieldSummaryEntry[] {
  return state.template.fields
    .filter((f) => f.askable)
    .map((f) => {
      const fs = state.fields[f.id];
      return {
        field_id: f.id,
        status: fs?.status ?? "unset",
        value: wireValue(fs?.value ?? null),
        asked_count: fs?.askedCount ?? 0,
        required: f.required,
      };
    });
}

/**
 * Owns async orchestration ONLY (§8) — every state mutation still goes
 * through interviewReducer via dispatch; this hook never writes to state
 * directly. AbortController on reset/unmount; an in-flight guard so a
 * double-tapped send cannot interleave two proposals.
 */
export function useInterviewRunner() {
  const { state, dispatch } = useInterviewState();
  const stateRef = useRef(state);
  stateRef.current = state;

  const inFlightRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const startInterview = useCallback(async () => {
    const current = stateRef.current;
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await api.start(current.template, current.locale, controller.signal);
      dispatch({ type: "BOOTSTRAPPED", continuityToken: res.continuity_token });
    } catch {
      // A failed bootstrap leaves phase as-is; RunCheckIn shows a retry affordance.
    }
  }, [dispatch]);

  const sendMessage = useCallback(
    async (text: string, inputMode: InputMode = "typed") => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (inFlightRef.current) return; // in-flight guard: no interleaved proposals
      const current = stateRef.current;
      if (current.phase === "paused" || current.phase === "complete") return;
      if (!current.continuityToken) return;

      inFlightRef.current = true;
      const target = nextTarget(current.template, current.fields);
      const participantTurnCount = current.meta.turnCount; // BEFORE this message — matches the token's current `n`

      dispatch({ type: "PARTICIPANT_MESSAGE", text: trimmed, inputMode, at: new Date().toISOString() });

      const transcript = [...current.turns, { role: "participant" as const, text: trimmed }]
        .slice(-TRANSCRIPT_WINDOW)
        .map((t) => ({ role: t.role, text: t.text }));

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await api.turn(
          {
            locale: current.locale,
            continuity_token: current.continuityToken,
            participant_turn_count: participantTurnCount,
            template: current.template,
            field_summary: buildFieldSummary(current),
            target_field_id: target?.id ?? null,
            transcript,
            latest_participant_message: trimmed,
          },
          controller.signal,
        );

        dispatch({
          type: "PROPOSAL_RECEIVED",
          response,
          at: new Date().toISOString(),
          targetFieldId: target?.id ?? null,
        });
      } catch (e) {
        const aborted = e instanceof DOMException && e.name === "AbortError";
        if (!aborted) {
          dispatch({ type: "PROPOSAL_FAILED", error: e instanceof Error ? e.message : "unknown_error" });
        }
      } finally {
        inFlightRef.current = false;
      }
    },
    [dispatch],
  );

  const finalizeFollowup = useCallback(async () => {
    const current = stateRef.current;
    if (!current.continuityToken) return;

    try {
      const res = await api.followup({
        continuity_token: current.continuityToken,
        participant_turn_count: current.meta.turnCount,
        template: current.template,
        locale: current.locale,
        field_summary: buildFieldSummary(current),
      });
      dispatch({ type: "FOLLOWUP_VERDICT", followup: res.followup });
    } catch {
      // Best-effort at finalization — the /turn-derived followup verdict already in state stands if this fails.
    }
  }, [dispatch]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "RESET" });
  }, [dispatch]);

  return { state, dispatch, startInterview, sendMessage, finalizeFollowup, reset };
}
